import { tenant } from '@tenant';
import { default as Tabs } from 'antd/es/tabs';
import { useMemo, useState } from 'react';

import { hasValue } from 'utils/hasValue';

import { useGetCryptoBalances } from 'api/customerApi';

import { useOrganisationId } from 'hooks/context';

import { SelectVirtualAccount } from '@molecules/SelectVirtualAccount';

import {
  CRYPTO_FUNDING_ACCOUNT_SELECTOR_TEST_ID,
  CRYPTO_FUNDING_CONTROL_PANEL_TEST_ID,
  CRYPTO_FUNDING_HISTORY_PANEL_TEST_ID,
  CRYPTO_FUNDING_NO_ELIGIBLE_ACCOUNTS_TEST_ID,
  CRYPTO_FUNDING_WIDGET_TEST_ID
} from './constants';
import { DepositsTable } from './deposit/DepositsTable';
import { DepositTab } from './deposit/DepositTab';
import { ExchangesTable } from './exchange/ExchangesTable';
import { ExchangeTab } from './exchange/ExchangeTab';
import { useVirtualBankAccount } from './shared/VirtualBankAccountProvider';
import { TransactionsTable } from './transactions/TransactionsTable';

import { CryptoProviderAttribution } from '../CryptoProviderAttribution';

type ActionTabKey = 'top-up-wallet' | 'request-quote';

export const CryptoWidget = () => {
  const organisationId = useOrganisationId<number>();
  const [activeActionTab, setActiveActionTab] =
    useState<ActionTabKey>('top-up-wallet');
  const [activeTableTab, setActiveTableTab] = useState('all');
  const {
    cryptoProvider,
    accounts,
    isAccountsLoading,
    virtualAccountNumber,
    setVirtualAccountNumber
  } = useVirtualBankAccount();

  const selectedAccount = useMemo(
    () =>
      accounts.find(
        (account) => account.account_number === virtualAccountNumber
      ),
    [accounts, virtualAccountNumber]
  );
  const hasSelectedAccount = hasValue(selectedAccount);
  const showNoEligibleAccounts = !isAccountsLoading && accounts.length === 0;
  const showAllTableTab = cryptoProvider === 'layer1';
  const activeTableTabKey =
    !showAllTableTab && activeTableTab === 'all' ? 'trades' : activeTableTab;
  const { data: balances } = useGetCryptoBalances(
    organisationId,
    { virtual_account_number: virtualAccountNumber },
    {
      query: {
        enabled: hasSelectedAccount,
        refetchInterval: 10_000,
        refetchOnWindowFocus: false
      }
    }
  );
  const actionTabs = [
    {
      key: 'top-up-wallet',
      label: 'Top-up wallet',
      children:
        activeActionTab === 'top-up-wallet' ? (
          <DepositTab balances={balances} />
        ) : null
    },
    {
      key: 'request-quote',
      label: 'Exchange',
      children:
        activeActionTab === 'request-quote' ? (
          <ExchangeTab balances={balances} />
        ) : null
    }
  ];
  const tableTabs = [
    ...(showAllTableTab
      ? [
          {
            key: 'all',
            label: 'All',
            children: (
              <TransactionsTable isActive={activeTableTabKey === 'all'} />
            )
          }
        ]
      : []),
    {
      key: 'trades',
      label: 'Trades',
      children: <ExchangesTable isActive={activeTableTabKey === 'trades'} />
    },
    {
      key: 'deposits',
      label: 'Deposits',
      children: <DepositsTable isActive={activeTableTabKey === 'deposits'} />
    }
  ];

  return (
    <div
      className="flex h-[670px] w-full gap-6 overflow-hidden rounded-xl bg-surface-muted p-4"
      data-testid={CRYPTO_FUNDING_WIDGET_TEST_ID}
    >
      <div
        className="flex min-h-0 w-[440px] flex-none flex-col rounded-xl bg-white px-6 py-4"
        data-testid={CRYPTO_FUNDING_CONTROL_PANEL_TEST_ID}
      >
        {showNoEligibleAccounts ? (
          <p
            className="text-sm text-content-secondary"
            data-testid={CRYPTO_FUNDING_NO_ELIGIBLE_ACCOUNTS_TEST_ID}
          >
            No accounts are configured for crypto funding. Please contact
            support at{' '}
            <a href={`mailto:${tenant.support.email}`}>
              {tenant.support.email}
            </a>
            .
          </p>
        ) : (
          <div className="mb-4 flex flex-col gap-1">
            <label className="text-xs font-semibold text-content-secondary">
              Account
            </label>
            <SelectVirtualAccount
              onChangeCallback={setVirtualAccountNumber}
              accounts={accounts}
              allowAll={false}
              currentAccountNumber={virtualAccountNumber}
              className="h-12 !w-full"
              e2eId={CRYPTO_FUNDING_ACCOUNT_SELECTOR_TEST_ID}
              loading={isAccountsLoading}
            />
          </div>
        )}
        {hasSelectedAccount && (
          <>
            <Tabs
              className="flex min-h-0 flex-1 flex-col [&_.ant-tabs-content]:h-full [&_.ant-tabs-tabpane]:h-full"
              activeKey={activeActionTab}
              onChange={(key) => setActiveActionTab(key as ActionTabKey)}
              items={actionTabs}
            />
          </>
        )}
        {hasValue(cryptoProvider) && (
          <CryptoProviderAttribution provider={cryptoProvider} />
        )}
      </div>
      {hasSelectedAccount && (
        <div
          className="min-w-0 flex-1 rounded-xl bg-white px-6 pb-6 pt-2"
          data-testid={CRYPTO_FUNDING_HISTORY_PANEL_TEST_ID}
        >
          <Tabs
            activeKey={activeTableTabKey}
            onChange={setActiveTableTab}
            items={tableTabs}
          />
        </div>
      )}
    </div>
  );
};
